import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Alert, Modal } from 'react-bootstrap';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import './Login.css';

function SetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);

  useEffect(() => {
    // Check if this is a password recovery flow
    const checkRecoveryMode = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        setIsRecoveryMode(true);
        setUserEmail(session.user.email);
      } else {
        // If no session, redirect to login
        navigate('/login');
      }
    };

    checkRecoveryMode();
  }, [navigate]);

  // Calculate password requirements in real-time
  const passwordRequirements = useMemo(() => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
    };
  }, [password]);

  const isPasswordValid = useMemo(() => {
    return Object.values(passwordRequirements).every(Boolean);
  }, [passwordRequirements]);

  const doPasswordsMatch = useMemo(() => {
    return password === confirmPassword && confirmPassword.length > 0;
  }, [password, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password
    if (!isPasswordValid) {
      setError('Please meet all password requirements');
      return;
    }

    // Check passwords match
    if (!doPasswordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      // Update user password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      // Sign out the user so they can log in with their new password
      await supabase.auth.signOut();

      // Redirect to login with success message
      navigate('/login?passwordSet=true');
    } catch (err) {
      console.error('Error setting password:', err);
      setError(err.message || 'Failed to set password. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!isRecoveryMode) {
    return null; // Will redirect to login
  }

  return (
    <div className="login-page">
      <Modal show={true} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Set Up Your Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>Welcome to ROE Barcode System!</strong>
            <p className="mb-0 mt-2">
              Please create a secure password for your account ({userEmail}).
              You'll use this password along with your email to sign in.
            </p>
          </Alert>

          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setPasswordTouched(true)}
                disabled={isSubmitting}
                required
                autoFocus
                className={passwordTouched ? (isPasswordValid ? 'is-valid' : 'is-invalid') : ''}
              />

              {/* Real-time password requirements */}
              <div className="mt-2">
                <small className={passwordRequirements.minLength ? 'text-success' : 'text-muted'}>
                  {passwordRequirements.minLength ? '✓' : '○'} At least 8 characters
                </small><br />
                <small className={passwordRequirements.hasUppercase ? 'text-success' : 'text-muted'}>
                  {passwordRequirements.hasUppercase ? '✓' : '○'} One uppercase letter
                </small><br />
                <small className={passwordRequirements.hasLowercase ? 'text-success' : 'text-muted'}>
                  {passwordRequirements.hasLowercase ? '✓' : '○'} One lowercase letter
                </small><br />
                <small className={passwordRequirements.hasNumber ? 'text-success' : 'text-muted'}>
                  {passwordRequirements.hasNumber ? '✓' : '○'} One number
                </small>
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setConfirmTouched(true)}
                disabled={isSubmitting}
                required
                className={confirmTouched ? (doPasswordsMatch ? 'is-valid' : 'is-invalid') : ''}
              />
              {confirmTouched && !doPasswordsMatch && confirmPassword.length > 0 && (
                <Form.Text className="text-danger">
                  Passwords do not match
                </Form.Text>
              )}
            </Form.Group>

            <div className="d-grid">
              <Button
                variant="primary"
                type="submit"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Setting Password...' : 'Set Password'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default SetPassword;
